/**
 * React Query hooks for the GHL Middleware Dashboard.
 * Follows the same patterns as useAnalytics.ts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  type GHLAnalysis,
  type GHLDashboardStats,
  type GHLDailyStats,
  type GHLQueueStatus,
  type GHLLocationSummary,
  type GHLLocationOverview,
  type GHLFollowUpRow,
  type GHLFollowUpGroupedRow,
  type GHLFollowUpStats,
} from '@/lib/ghl-dashboard'
import type { TimeRange } from '@/components/ghl-dashboard/TimeRangeFilter'

const FEED_REFRESH_INTERVAL = 15 * 1000
const QUEUE_REFRESH_INTERVAL = 5 * 1000
const STATS_REFRESH_INTERVAL = 60 * 1000
const PAGE_SIZE_DEFAULT = 25

/**
 * Live activity feed — one row per opportunity (or bare contact when no
 * opportunity exists), showing the chronologically latest analysis per group,
 * filtered by time range, optional location, and optional recommended action.
 *
 * Uses the get_ghl_activity_feed_grouped RPC for server-side grouping so
 * pagination reflects opportunity count, not raw analysis count.
 *
 * @param actionFilter - When set, restricts the feed to rows whose
 *   `recommended_action` matches this value (e.g., `MOVE_TO_BOOKED`).
 *   `null` / `undefined` returns all actions.
 */
export function useGHLActivityFeed(
  timeRange: TimeRange,
  page: number,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true,
  locationId?: string,
  actionFilter?: string | null
) {
  return useQuery<{ data: GHLAnalysis[]; count: number }>({
    queryKey: ['admin', 'ghl-feed', timeRange.from.toISOString(), timeRange.until.toISOString(), page, pageSize, locationId ?? '', actionFilter ?? ''],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_activity_feed_grouped', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
        p_limit: pageSize,
        p_offset: page * pageSize,
        p_action_filter: actionFilter || null,
      })

      if (error) throw new Error(error.message)

      const result = data as unknown as { data: GHLAnalysis[]; count: number }
      return {
        data: result.data ?? [],
        count: result.count ?? 0,
      }
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

/**
 * Aggregate dashboard stats via RPC. Optional location scoping.
 */
export function useGHLDashboardStats(timeRange: TimeRange, enabled = true, locationId?: string) {
  return useQuery<GHLDashboardStats | null>({
    queryKey: ['admin', 'ghl-stats', timeRange.from.toISOString(), timeRange.until.toISOString(), locationId ?? ''],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_dashboard_stats', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
      })

      if (error) throw new Error(error.message)
      return data as unknown as GHLDashboardStats
    },
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Daily time-series stats via RPC. Optional location scoping.
 */
export function useGHLDailyStats(timeRange: TimeRange, enabled = true, locationId?: string) {
  return useQuery<GHLDailyStats[]>({
    queryKey: ['admin', 'ghl-daily-stats', timeRange.from.toISOString(), timeRange.until.toISOString(), locationId ?? ''],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_daily_stats', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
      })

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as GHLDailyStats[]
    },
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Analyses filtered by location.
 * @deprecated Use useGHLActivityFeed with locationId parameter instead.
 */
export function useGHLLocationAnalyses(
  timeRange: TimeRange,
  locationId: string,
  page: number,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true
) {
  return useGHLActivityFeed(timeRange, page, pageSize, enabled && !!locationId, locationId)
}

/**
 * Location name lookup map.
 * Reads from ghl_location_names cache table (populated by middleware).
 */
export function useGHLLocationNames() {
  return useQuery<Map<string, string>>({
    queryKey: ['admin', 'ghl-location-names'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ghl_location_names')
        .select('location_id, name')

      if (error) throw new Error(error.message)

      const map = new Map<string, string>()
      for (const row of data ?? []) {
        map.set(row.location_id, row.name)
      }
      return map
    },
    staleTime: 5 * 60 * 1000,
  })
}

export interface GHLContactName {
  readonly firstName: string | null
  readonly lastName: string | null
  readonly fullName: string | null
  readonly email: string | null
  readonly phone: string | null
}

/**
 * Contact name lookup for a specific set of IDs (bounded by the currently-
 * visible page). Reads from `ghl_contacts_cache` first; for any IDs missing
 * from the cache, dispatches a call to the `resolve-ghl-contacts` edge
 * function which fetches from GHL, writes the cache, and returns the result.
 *
 * Deliberately scoped to the page's contactIds — does NOT bulk-fetch all
 * contacts for the location. Operator sees names for visible rows only.
 *
 * @param contactIds  The IDs currently on screen. Empty array = no-op.
 * @param locationId  Required when dispatching to the resolver (used to
 *                    pick the right OAuth token).
 */
export function useGHLContactNames(
  contactIds: readonly string[],
  locationId?: string
) {
  const queryClient = useQueryClient()

  // Stable key from sorted unique IDs so queryKey equality works across renders
  const key = [...new Set(contactIds)].filter(Boolean).sort().join(',')

  return useQuery<Map<string, GHLContactName>>({
    queryKey: ['admin', 'ghl-contact-names', key],
    enabled: key.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const ids = key.split(',').filter(Boolean)
      const map = new Map<string, GHLContactName>()

      if (ids.length === 0) return map

      // --- 1. Read the cache ---
      const { data: cached, error: cacheErr } = await supabase
        .from('ghl_contacts_cache')
        .select('contact_id, first_name, last_name, full_name, email, phone')
        .in('contact_id', ids)

      if (cacheErr) throw new Error(cacheErr.message)

      const cachedIds = new Set<string>()
      for (const row of cached ?? []) {
        map.set(row.contact_id, {
          firstName: row.first_name,
          lastName: row.last_name,
          fullName: row.full_name,
          email: row.email,
          phone: row.phone,
        })
        cachedIds.add(row.contact_id)
      }

      // --- 2. Resolve missing IDs via edge function (if locationId provided) ---
      const missing = ids.filter((id) => !cachedIds.has(id))
      if (missing.length === 0 || !locationId) return map

      try {
        const { data: resolvedPayload, error: resolveErr } =
          await supabase.functions.invoke('resolve-ghl-contacts', {
            body: { contactIds: missing, locationId },
          })

        if (resolveErr) {
          // Non-fatal — return what we have; unresolved contacts show "Unknown"
          console.warn('[useGHLContactNames] resolver error:', resolveErr.message)
          return map
        }

        const contacts =
          (resolvedPayload as { contacts?: Record<string, GHLContactName> })?.contacts ?? {}
        for (const [id, name] of Object.entries(contacts)) {
          map.set(id, name)
        }

        // Invalidate sibling queries so other components see the new cache rows
        queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-contact-names'] })
      } catch (err) {
        console.warn('[useGHLContactNames] resolver exception:', err)
        // Fall through — return partial map
      }

      return map
    },
  })
}

/**
 * Distinct locations with analysis counts + resolved names.
 */
export function useGHLLocations() {
  return useQuery<GHLLocationSummary[]>({
    queryKey: ['admin', 'ghl-locations'],
    queryFn: async () => {
      const { data: analyses, error: aErr } = await supabase
        .from('ghl_conversation_analyses')
        .select('location_id')

      if (aErr) throw new Error(aErr.message)

      const counts = new Map<string, number>()
      for (const row of analyses ?? []) {
        const loc = row.location_id as string
        counts.set(loc, (counts.get(loc) ?? 0) + 1)
      }

      const { data: names } = await supabase
        .from('ghl_location_names')
        .select('location_id, name')

      const nameMap = new Map<string, string>()
      for (const row of names ?? []) {
        nameMap.set(row.location_id, row.name)
      }

      return [...counts.entries()]
        .map(([location_id, count]) => ({
          location_id,
          count,
          name: nameMap.get(location_id),
        }))
        .sort((a, b) => b.count - a.count)
    },
    staleTime: 60 * 1000,
  })
}

/**
 * Location overview for the location list landing page.
 * Returns per-location stats (analysis count, bookings, pending follow-ups, AI cost).
 */
export function useGHLLocationOverview(timeRange: TimeRange, enabled = true) {
  return useQuery<GHLLocationOverview[]>({
    queryKey: ['admin', 'ghl-location-overview', timeRange.from.toISOString(), timeRange.until.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_location_overview', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
      })

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as GHLLocationOverview[]
    },
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Full analysis detail for expandable row (includes raw data).
 */
export function useGHLAnalysisDetail(id: string, enabled = true) {
  return useQuery<GHLAnalysis | null>({
    queryKey: ['admin', 'ghl-analysis', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ghl_conversation_analyses')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw new Error(error.message)
      return data as unknown as GHLAnalysis
    },
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Sibling analyses — all rows in the same group as a given analysis, ordered
 * oldest → newest. Used by the detail panel to render a timeline of what
 * happened to this contact/opportunity over time (welcome SMS → Claude
 * analysis → stage change → …).
 *
 * The group key is `COALESCE(conversation_id, contact_id)` — same key the
 * feed's `get_ghl_activity_feed_grouped` RPC groups by. Conversation comes
 * first so repeat-customer rows all fold into one group (even when they have
 * multiple opportunities). Contact is the fallback for bare events with no
 * conversation (e.g. early stage changes before the middleware backfill).
 */
export function useGHLAnalysisSiblings(analysis: GHLAnalysis | null | undefined, enabled = true) {
  const groupKey = analysis
    ? (analysis.conversation_id ?? analysis.contact_id)
    : null

  return useQuery<GHLAnalysis[]>({
    queryKey: ['admin', 'ghl-analysis-siblings', groupKey, analysis?.location_id ?? ''],
    queryFn: async () => {
      if (!groupKey) return []

      const { data, error } = await supabase.rpc('get_ghl_analysis_siblings', {
        p_group_key: groupKey,
        p_location_id: analysis?.location_id ?? null,
      })

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as GHLAnalysis[]
    },
    enabled: enabled && !!groupKey,
    staleTime: 10 * 1000,
  })
}

/**
 * Queue status from middleware health endpoint.
 */
export function useGHLQueueStatus(middlewareUrl: string | null, enabled = true) {
  return useQuery<GHLQueueStatus | null>({
    queryKey: ['admin', 'ghl-queue'],
    queryFn: async () => {
      if (!middlewareUrl) return null

      const response = await fetch(`${middlewareUrl}/health/queue`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      })

      if (!response.ok) return null
      return response.json() as Promise<GHLQueueStatus>
    },
    refetchInterval: QUEUE_REFRESH_INTERVAL,
    staleTime: 3 * 1000,
    enabled: enabled && !!middlewareUrl,
    retry: false,
  })
}

// ---------------------------------------------------------------------------
// Follow-Up Automation Hooks
// ---------------------------------------------------------------------------

/**
 * Follow-up feed grouped by opportunity — one row per opportunity (soonest step)
 * with step counts. Uses an RPC for server-side grouping so pagination is accurate
 * (page size = opportunities, not individual steps).
 */
export function useGHLFollowUpFeed(
  timeRange: TimeRange,
  page: number,
  statusFilter: string,
  locationId: string,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true
) {
  return useQuery<{ data: GHLFollowUpGroupedRow[]; count: number }>({
    queryKey: ['admin', 'ghl-followup-feed', timeRange.from.toISOString(), timeRange.until.toISOString(), page, pageSize, statusFilter, locationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_followup_feed_grouped', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
        p_status_filter: statusFilter || null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      })

      if (error) throw new Error(error.message)

      const result = data as unknown as { data: GHLFollowUpGroupedRow[]; count: number }
      return {
        data: result.data ?? [],
        count: result.count ?? 0,
      }
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

/**
 * Follow-up aggregate stats via RPC. Optional location scoping.
 */
export function useGHLFollowUpStats(timeRange: TimeRange, enabled = true, locationId?: string) {
  return useQuery<GHLFollowUpStats | null>({
    queryKey: ['admin', 'ghl-followup-stats', timeRange.from.toISOString(), timeRange.until.toISOString(), locationId ?? ''],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_followup_stats', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
      })

      if (error) throw new Error(error.message)
      return data as unknown as GHLFollowUpStats
    },
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Follow-up timeline — all steps for a specific opportunity.
 */
export function useGHLFollowUpTimeline(opportunityId: string, enabled = true) {
  return useQuery<GHLFollowUpRow[]>({
    queryKey: ['admin', 'ghl-followup-timeline', opportunityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ghl_followup_schedule')
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('step_number', { ascending: true })

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as GHLFollowUpRow[]
    },
    staleTime: 10 * 1000,
    enabled: enabled && !!opportunityId,
  })
}

/**
 * Awaiting mark-lost — opportunities currently sitting in the per-location
 * mark-lost grace window. Backed by `ghl_followup_schedule` step-99 rows
 * (MARK_LOST_GRACE_STEP) with status='pending'.
 *
 * Each pending row was inserted by `executeMarkLost` after Claude detected
 * a customer decline. The cron fires the row when its `fire_at` passes; if
 * the customer shows new booking intent before then, the row is cancelled
 * (cancel-path, not visible here).
 *
 * Not paginated — the queue is small in steady state. If hundreds of rows
 * accumulate, the cron is stalled and a flat list is fine for triage.
 */
export function useGHLAwaitingMarkLost(
  locationId?: string,
  limit = 100,
  enabled = true
) {
  return useQuery<GHLFollowUpRow[]>({
    queryKey: ['admin', 'ghl-awaiting-mark-lost', locationId ?? '', limit],
    queryFn: async () => {
      let query = supabase
        .from('ghl_followup_schedule')
        .select('*')
        .eq('step_number', 99)
        .eq('status', 'pending')
        .order('fire_at', { ascending: true })
        .limit(limit)

      if (locationId) {
        query = query.eq('location_id', locationId)
      }

      const { data, error } = await query

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as GHLFollowUpRow[]
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

/**
 * Marked-lost history — opportunities whose grace window expired and the
 * lost-status write fired. Backed by `ghl_followup_schedule` step-99 rows
 * with status='sent'. Filtered by the dashboard time range using
 * `last_attempt_at` (when the cron actually performed the write), which is
 * the semantically-correct "when did this become lost" column for sent rows.
 *
 * Paginated like the activity feed.
 */
export function useGHLMarkedLostHistory(
  timeRange: TimeRange,
  page: number,
  locationId?: string,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true
) {
  return useQuery<{ data: GHLFollowUpRow[]; count: number }>({
    queryKey: ['admin', 'ghl-marked-lost-history', timeRange.from.toISOString(), timeRange.until.toISOString(), page, pageSize, locationId ?? ''],
    queryFn: async () => {
      const from = page * pageSize
      const to = from + pageSize - 1

      let query = supabase
        .from('ghl_followup_schedule')
        .select('*', { count: 'exact' })
        .eq('step_number', 99)
        .eq('status', 'sent')
        .gte('last_attempt_at', timeRange.from.toISOString())
        .lte('last_attempt_at', timeRange.until.toISOString())
        .order('last_attempt_at', { ascending: false })

      if (locationId) {
        query = query.eq('location_id', locationId)
      }

      const { data, error, count } = await query.range(from, to)

      if (error) throw new Error(error.message)
      return { data: (data ?? []) as unknown as GHLFollowUpRow[], count: count ?? 0 }
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Follow-Up Cancel Mutations
// ---------------------------------------------------------------------------

/**
 * Cancel a single follow-up step.
 */
export function useCancelFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { id: string; reason?: string }) => {
      const { error } = await supabase
        .from('ghl_followup_schedule')
        .update({
          status: 'cancelled',
          skip_reason: params.reason ?? 'manually_cancelled_from_dashboard',
        })
        .eq('id', params.id)
        .eq('status', 'pending')

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-followup-feed'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-followup-stats'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-followup-timeline'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-location-overview'] })
    },
  })
}

/**
 * Cancel all pending follow-up steps for an opportunity.
 */
export function useCancelAllFollowUps() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { opportunityId: string; reason?: string }) => {
      const { error } = await supabase
        .from('ghl_followup_schedule')
        .update({
          status: 'cancelled',
          skip_reason: params.reason ?? 'manually_cancelled_from_dashboard',
        })
        .eq('opportunity_id', params.opportunityId)
        .in('status', ['pending', 'processing'])

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-followup-feed'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-followup-stats'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-followup-timeline'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-location-overview'] })
    },
  })
}
